import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import ProjectAutocomplete from '@/components/ProjectAutocomplete';
import { 
  Settings as SettingsIcon, 
  Tag, 
  Clock, 
  FolderKanban,
  Plus, 
  Trash2, 
  Edit2, 
  Save,
  X,
  RefreshCw,
  AlertCircle,
  Mail,
  Send,
  CheckCircle2,
  Calendar,
  Link,
  Unlink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Opzioni per la matrice SLA
const IMPACT_OPTIONS = [
  { value: 'singolo_utente', label: 'Singolo Utente' },
  { value: 'gruppo_utenti', label: 'Gruppo di Utenti' },
  { value: 'tutti_utenti', label: 'Tutti gli Utenti' }
];

const CRITICALITY_OPTIONS = [
  { value: 'posso_continuare', label: 'Posso Continuare a Lavorare' },
  { value: 'non_posso_continuare', label: 'Non Posso Continuare' }
];

const EXTENSION_OPTIONS = [
  { value: 'singolo_paziente', label: 'Singolo Paziente' },
  { value: 'gruppo_pazienti', label: 'Gruppo di Pazienti' },
  { value: 'tutti_pazienti', label: 'Tutti i Pazienti' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa', color: 'bg-gray-500' },
  { value: 'normal', label: 'Normale', color: 'bg-blue-500' },
  { value: 'high', label: 'Alta', color: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgente', color: 'bg-red-500' }
];

function Settings({ token }) {
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Categories state
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#3b82f6', active: true });

  // SLA Matrix state
  const [slaRules, setSlaRules] = useState([]);
  const [editingSla, setEditingSla] = useState(null);
  const [newSlaRule, setNewSlaRule] = useState({
    project_name: '',  // Empty = global rule
    impact: '',
    criticality: '',
    extension: '',
    priority: 'normal',
    deadline_hours: 24
  });

  // Project Settings state
  const [projectSettings, setProjectSettings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [newProjectSetting, setNewProjectSetting] = useState({
    project_name: '',
    default_assignee_id: null,
    default_assignee_name: '',
    default_deadline_hours: 48,
    auto_assign: false,
    notifications_enabled: true
  });

  // SMTP Settings state
  const [smtpSettings, setSmtpSettings] = useState({
    enabled: false,
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    from_email: '',
    from_name: 'MantisBT Portal'
  });
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  // Google Calendar state
  const [googleStatus, setGoogleStatus] = useState({ connected: false, google_email: null });
  const [googleLoading, setGoogleLoading] = useState(false);

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: '', name: '' });

  useEffect(() => {
    fetchCategories();
    fetchSlaRules();
    fetchProjectSettings();
    fetchProjects();
    fetchUsers();
    fetchSmtpSettings();
    fetchGoogleStatus();
  }, []);

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess('');
    } else {
      setSuccess(msg);
      setError('');
    }
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3000);
  };

  // ============================================
  // Categories Functions
  // ============================================

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      showMessage('Il nome della categoria è obbligatorio', true);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/settings/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCategory({ name: '', description: '', color: '#3b82f6', active: true });
      fetchCategories();
      showMessage('Categoria creata con successo');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nella creazione', true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (category) => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/settings/categories/${category.id}`, category, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingCategory(null);
      fetchCategories();
      showMessage('Categoria aggiornata');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nell\'aggiornamento', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setDeleteConfirm({ show: true, type: 'category', id: categoryId, name: 'questo tag' });
  };

  const confirmDeleteCategory = async (categoryId) => {
    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/settings/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCategories();
      showMessage('Tag eliminato');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nell\'eliminazione', true);
    } finally {
      setLoading(false);
      setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    }
  };

  // ============================================
  // SLA Matrix Functions
  // ============================================

  const fetchSlaRules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/sla-matrix`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlaRules(response.data.rules || []);
    } catch (err) {
      console.error('Error fetching SLA rules:', err);
    }
  };

  const handleCreateSlaRule = async () => {
    if (!newSlaRule.impact || !newSlaRule.criticality || !newSlaRule.extension) {
      showMessage('Impatto, Criticità e Estensione sono obbligatori', true);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/settings/sla-matrix`, newSlaRule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewSlaRule({
        project_name: '',
        impact: '',
        criticality: '',
        extension: '',
        priority: 'normal',
        deadline_hours: 24
      });
      fetchSlaRules();
      showMessage('Regola SLA creata');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nella creazione', true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSlaRule = async (rule) => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/settings/sla-matrix/${rule.id}`, rule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingSla(null);
      fetchSlaRules();
      showMessage('Regola SLA aggiornata');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nell\'aggiornamento', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlaRule = async (ruleId) => {
    setDeleteConfirm({ show: true, type: 'sla', id: ruleId, name: 'questa regola SLA' });
  };

  const confirmDeleteSlaRule = async (ruleId) => {
    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/settings/sla-matrix/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSlaRules();
      showMessage('Regola SLA eliminata');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nell\'eliminazione', true);
    } finally {
      setLoading(false);
      setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    }
  };

  // ============================================
  // Project Settings Functions
  // ============================================

  const fetchProjectSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectSettings(response.data.settings || []);
    } catch (err) {
      console.error('Error fetching project settings:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchUsers = async (projectName = null) => {
    try {
      const params = projectName ? { project_name: projectName } : {};
      const response = await axios.get(`${BACKEND_URL}/api/settings/available-users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Fetch users when project is selected
  const handleProjectSelect = async (projectName) => {
    // Allow empty project name for global configuration
    setNewProjectSetting({...newProjectSetting, project_name: projectName || '', default_assignee_id: null, default_assignee_name: ''});
    if (projectName) {
      await fetchUsers(projectName);
    } else {
      // For global config, fetch all users
      await fetchUsers();
    }
  };

  const handleCreateProjectSetting = async () => {
    // Allow global configuration (empty project_name)
    const settingToSave = {
      ...newProjectSetting,
      project_name: newProjectSetting.project_name || '__GLOBAL__'  // Use special marker for global
    };
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/settings/projects`, settingToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewProjectSetting({
        project_name: '',
        default_assignee_id: null,
        default_assignee_name: '',
        default_deadline_hours: 48,
        auto_assign: false,
        notifications_enabled: true
      });
      fetchProjectSettings();
      showMessage('Impostazioni progetto salvate');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nel salvataggio', true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProjectSetting = async (setting) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/settings/projects`, setting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingProject(null);
      fetchProjectSettings();
      showMessage('Impostazioni aggiornate');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nell\'aggiornamento', true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProjectSetting = async (projectName) => {
    setDeleteConfirm({ show: true, type: 'project', id: projectName, name: `le impostazioni del progetto "${projectName}"` });
  };

  const confirmDeleteProjectSetting = async (projectName) => {
    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/settings/projects/${encodeURIComponent(projectName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProjectSettings();
      showMessage('Impostazioni eliminate');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nell\'eliminazione', true);
    } finally {
      setLoading(false);
      setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    }
  };

  // Handle confirm delete based on type
  const handleConfirmDelete = async () => {
    switch (deleteConfirm.type) {
      case 'category':
        await confirmDeleteCategory(deleteConfirm.id);
        break;
      case 'sla':
        await confirmDeleteSlaRule(deleteConfirm.id);
        break;
      case 'project':
        await confirmDeleteProjectSetting(deleteConfirm.id);
        break;
      default:
        setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    }
  };

  const getPriorityColor = (priority) => {
    const option = PRIORITY_OPTIONS.find(p => p.value === priority);
    return option?.color || 'bg-gray-500';
  };

  const getPriorityLabel = (priority) => {
    const option = PRIORITY_OPTIONS.find(p => p.value === priority);
    return option?.label || priority;
  };

  const getImpactLabel = (value) => IMPACT_OPTIONS.find(o => o.value === value)?.label || value;
  const getCriticalityLabel = (value) => CRITICALITY_OPTIONS.find(o => o.value === value)?.label || value;
  const getExtensionLabel = (value) => EXTENSION_OPTIONS.find(o => o.value === value)?.label || value;

  // ============================================
  // SMTP Functions
  // ============================================

  const fetchSmtpSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.settings) {
        setSmtpSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Error fetching SMTP settings:', err);
    }
  };

  const handleSaveSmtpSettings = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/settings/smtp`, smtpSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('Configurazione SMTP salvata');
      fetchSmtpSettings();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore nel salvataggio', true);
    } finally {
      setLoading(false);
    }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/settings/smtp/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmtpTestResult(response.data);
    } catch (err) {
      setSmtpTestResult({ success: false, message: err.response?.data?.detail || 'Errore nel test' });
    } finally {
      setSmtpTesting(false);
    }
  };

  // ============================
  // Google Calendar functions
  // ============================
  
  const fetchGoogleStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus(response.data);
    } catch (err) {
      console.error('Error fetching Google status:', err);
      setGoogleStatus({ connected: false });
    }
  };

  const handleConnectGoogle = async () => {
    setGoogleLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/google/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Open Google OAuth in new window
      const authWindow = window.open(response.data.authorization_url, 'google_auth', 'width=600,height=700');
      
      // Listen for postMessage from popup
      const messageHandler = async (event) => {
        if (event.data?.type === 'google_auth_success') {
          window.removeEventListener('message', messageHandler);
          showMessage(`Google Calendar connesso per ${event.data.email}`);
          await fetchGoogleStatus();
          setGoogleLoading(false);
        } else if (event.data?.type === 'google_auth_error') {
          window.removeEventListener('message', messageHandler);
          showMessage(event.data.error || 'Errore connessione Google', true);
          setGoogleLoading(false);
        }
      };
      window.addEventListener('message', messageHandler);
      
      // Also check if window closes without message
      const checkAuth = setInterval(async () => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            // Give time for message to arrive
            setTimeout(async () => {
              window.removeEventListener('message', messageHandler);
              setGoogleLoading(false);
              await fetchGoogleStatus();
            }, 500);
          }
        } catch (e) {
          // Window might be blocked by CORS
        }
      }, 1000);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkAuth);
        window.removeEventListener('message', messageHandler);
        setGoogleLoading(false);
      }, 300000);
      
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore connessione Google', true);
      setGoogleLoading(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setGoogleLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/google/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false });
      showMessage('Google Calendar disconnesso');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore disconnessione', true);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Conferma Eliminazione
            </h3>
            <p className="text-[rgb(161,161,170)] font-mono mb-6">
              Sei sicuro di voler eliminare {deleteConfirm.name}?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirm({ show: false, type: '', id: '', name: '' })}
                className="font-mono"
              >
                Annulla
              </Button>
              <Button
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white font-mono"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-gradient flex items-center gap-3">
            <SettingsIcon className="h-8 w-8" />
            Impostazioni
          </h1>
          <p className="text-[rgb(161,161,170)] font-mono mt-1">
            Gestione categorie, matrice SLA e impostazioni progetti
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500 rounded p-4">
          <p className="text-red-500 font-mono flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </p>
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500 rounded p-4">
          <p className="text-green-500 font-mono">{success}</p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] flex-wrap h-auto gap-1 p-1">
          <TabsTrigger
            value="categories"
            className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
          >
            <Tag className="h-4 w-4 mr-2" />
            Tag
          </TabsTrigger>
          <TabsTrigger
            value="sla"
            className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
          >
            <Clock className="h-4 w-4 mr-2" />
            Matrice SLA
          </TabsTrigger>
          <TabsTrigger
            value="projects"
            className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
          >
            <FolderKanban className="h-4 w-4 mr-2" />
            Impostazioni Progetti
          </TabsTrigger>
          <TabsTrigger
            value="smtp"
            className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email SMTP
          </TabsTrigger>
          <TabsTrigger
            value="google"
            className="data-[state=active]:bg-[rgb(59,130,246)] data-[state=active]:text-white font-mono"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Google Calendar
          </TabsTrigger>
        </TabsList>

        {/* ============================================ */}
        {/* Tags Tab (formerly Categories) */}
        {/* ============================================ */}
        <TabsContent value="categories" className="mt-6">
          <div className="space-y-6">
            {/* Add new tag */}
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6">
              <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)] mb-4">
                Nuovo Tag
              </h3>
              <p className="text-sm text-[rgb(161,161,170)] font-mono mb-4">
                I tag sono testo libero e vengono associati alle segnalazioni. Tutte le segnalazioni avranno categoria &quot;Assistenza Tecnica&quot;.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Nome Tag *</label>
                  <Input
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                    placeholder="Nome tag"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Descrizione</label>
                  <Input
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                    placeholder="Descrizione"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Colore</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                      className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleCreateCategory}
                    disabled={loading}
                    className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi
                  </Button>
                </div>
              </div>
            </div>

            {/* Tags list */}
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[rgb(39,39,42)]">
                <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)]">
                  Tag Esistenti ({categories.length})
                </h3>
              </div>
              <div className="divide-y divide-[rgb(39,39,42)]">
                {categories.length === 0 ? (
                  <div className="p-8 text-center text-[rgb(161,161,170)] font-mono">
                    Nessun tag configurato
                  </div>
                ) : (
                  categories.map((cat) => (
                    <div key={cat.id} className="p-4 flex items-center justify-between hover:bg-[rgb(39,39,42)]/50">
                      {editingCategory?.id === cat.id ? (
                        <div className="flex-1 flex items-center gap-4">
                          <Input
                            value={editingCategory.name}
                            onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                            className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono max-w-xs"
                          />
                          <Input
                            value={editingCategory.description}
                            onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                            className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono max-w-xs"
                          />
                          <input
                            type="color"
                            value={editingCategory.color}
                            onChange={(e) => setEditingCategory({...editingCategory, color: e.target.value})}
                            className="w-10 h-8 rounded cursor-pointer"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateCategory(editingCategory)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingCategory(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-4">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: cat.color }}
                            />
                            <div>
                              <span className="text-[rgb(250,250,250)] font-mono font-semibold">
                                {cat.name}
                              </span>
                              {cat.description && (
                                <span className="text-[rgb(161,161,170)] font-mono text-sm ml-2">
                                  - {cat.description}
                                </span>
                              )}
                            </div>
                            {!cat.active && (
                              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded font-mono">
                                Disattivata
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingCategory({...cat})}
                              className="text-[rgb(161,161,170)] hover:text-white"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteCategory(cat.id)}
                              className="text-red-500 hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ============================================ */}
        {/* SLA Matrix Tab */}
        {/* ============================================ */}
        <TabsContent value="sla" className="mt-6">
          <div className="space-y-6">
            {/* Add new SLA rule */}
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6">
              <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)] mb-2">
                Nuova Regola SLA
              </h3>
              <p className="text-sm text-[rgb(161,161,170)] font-mono mb-4">
                Le regole per progetto specifico hanno priorità su quelle globali. Lascia il progetto vuoto per creare una regola globale.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Progetto</label>
                  <ProjectAutocomplete
                    projects={projects.map(p => p.name)}
                    value={newSlaRule.project_name}
                    onChange={(value) => setNewSlaRule({...newSlaRule, project_name: value})}
                    placeholder="Globale (default)"
                    allowAll={true}
                    className="w-full"
                  />
                  {!newSlaRule.project_name && (
                    <span className="text-xs text-yellow-400 font-mono mt-1 block">🌐 Regola globale</span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Impatto *</label>
                  <Select
                    value={newSlaRule.impact}
                    onValueChange={(value) => setNewSlaRule({...newSlaRule, impact: value})}
                  >
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                      {IMPACT_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="font-mono">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Criticità *</label>
                  <Select
                    value={newSlaRule.criticality}
                    onValueChange={(value) => setNewSlaRule({...newSlaRule, criticality: value})}
                  >
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                      {CRITICALITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="font-mono">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Estensione *</label>
                  <Select
                    value={newSlaRule.extension}
                    onValueChange={(value) => setNewSlaRule({...newSlaRule, extension: value})}
                  >
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                      {EXTENSION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="font-mono">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Priorità</label>
                  <Select
                    value={newSlaRule.priority}
                    onValueChange={(value) => setNewSlaRule({...newSlaRule, priority: value})}
                  >
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                      {PRIORITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="font-mono">
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${opt.color}`}></span>
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Ore Scadenza</label>
                  <Input
                    type="number"
                    value={newSlaRule.deadline_hours}
                    onChange={(e) => setNewSlaRule({...newSlaRule, deadline_hours: parseInt(e.target.value) || 0})}
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleCreateSlaRule}
                    disabled={loading}
                    className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi
                  </Button>
                </div>
              </div>
            </div>

            {/* SLA rules table */}
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[rgb(39,39,42)]">
                <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)]">
                  Regole SLA ({slaRules.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[rgb(39,39,42)]/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Progetto</th>
                      <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Impatto</th>
                      <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Criticità</th>
                      <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Estensione</th>
                      <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Priorità</th>
                      <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Scadenza</th>
                      <th className="px-4 py-3 text-right text-xs font-mono text-[rgb(161,161,170)] uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(39,39,42)]">
                    {slaRules.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-[rgb(161,161,170)] font-mono">
                          Nessuna regola SLA configurata
                        </td>
                      </tr>
                    ) : (
                      slaRules.map((rule) => (
                        <tr key={rule.id} className="hover:bg-[rgb(39,39,42)]/30">
                          <td className="px-4 py-3 text-sm font-mono">
                            {rule.project_name ? (
                              <span className="text-[rgb(250,250,250)]">{rule.project_name}</span>
                            ) : (
                              <span className="text-yellow-400">🌐 Globale</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-[rgb(250,250,250)]">
                            {getImpactLabel(rule.impact)}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-[rgb(250,250,250)]">
                            {getCriticalityLabel(rule.criticality)}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-[rgb(250,250,250)]">
                            {getExtensionLabel(rule.extension)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-mono text-white ${getPriorityColor(rule.priority)}`}>
                              {getPriorityLabel(rule.priority)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-[rgb(250,250,250)]">
                            {rule.deadline_hours}h
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSlaRule(rule.id)}
                              className="text-red-500 hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ============================================ */}
        {/* Project Settings Tab */}
        {/* ============================================ */}
        <TabsContent value="projects" className="mt-6">
          <div className="space-y-6">
            {/* Add new project setting */}
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6">
              <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)] mb-4">
                Nuova Configurazione Progetto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Progetto</label>
                  <ProjectAutocomplete
                    projects={projects.map(p => p.name)}
                    value={newProjectSetting.project_name}
                    onChange={handleProjectSelect}
                    placeholder="Globale (tutti i progetti)"
                    allowAll={true}
                    className="w-full"
                  />
                  {!newProjectSetting.project_name && (
                    <span className="text-xs text-yellow-400 font-mono mt-1 block">🌐 Configurazione globale</span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Assegnatario Default</label>
                  <Select
                    value={newProjectSetting.default_assignee_id?.toString() || 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setNewProjectSetting({
                          ...newProjectSetting,
                          default_assignee_id: null,
                          default_assignee_name: ''
                        });
                      } else {
                        const user = users.find(u => u.id.toString() === value);
                        setNewProjectSetting({
                          ...newProjectSetting,
                          default_assignee_id: parseInt(value) || null,
                          default_assignee_name: user?.real_name || user?.name || ''
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                      <SelectValue placeholder="Nessuno" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] max-h-60 overflow-y-auto">
                      <SelectItem value="none" className="font-mono">Nessuno</SelectItem>
                      {users.length === 0 && (
                        <div className="px-2 py-1 text-sm text-[rgb(161,161,170)]">Caricamento utenti...</div>
                      )}
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id.toString()} className="font-mono">
                          {user.real_name || user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Scadenza Default (ore)</label>
                  <Input
                    type="number"
                    value={newProjectSetting.default_deadline_hours}
                    onChange={(e) => setNewProjectSetting({...newProjectSetting, default_deadline_hours: parseInt(e.target.value) || 48})}
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 text-sm font-mono text-[rgb(161,161,170)]">
                    <Switch
                      checked={newProjectSetting.auto_assign}
                      onCheckedChange={(checked) => setNewProjectSetting({...newProjectSetting, auto_assign: checked})}
                    />
                    Auto-assegna
                  </label>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleCreateProjectSetting}
                    disabled={loading}
                    className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Salva
                  </Button>
                </div>
              </div>
            </div>

            {/* Project settings list */}
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[rgb(39,39,42)]">
                <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)]">
                  Impostazioni Progetti ({projectSettings.length})
                </h3>
              </div>
              <div className="divide-y divide-[rgb(39,39,42)]">
                {projectSettings.length === 0 ? (
                  <div className="p-8 text-center text-[rgb(161,161,170)] font-mono">
                    Nessuna configurazione progetto
                  </div>
                ) : (
                  projectSettings.map((setting) => (
                    <div key={setting.id} className="p-4 hover:bg-[rgb(39,39,42)]/30">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <div className="text-[rgb(250,250,250)] font-mono font-semibold text-lg">
                            {setting.project_name === '__GLOBAL__' ? (
                              <span className="flex items-center gap-2">
                                <span className="text-yellow-400">🌐</span>
                                Configurazione Globale
                              </span>
                            ) : setting.project_name}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-[rgb(161,161,170)] font-mono">
                            <span>
                              Assegnatario: {setting.default_assignee_name || 'Nessuno'}
                            </span>
                            <span>
                              Scadenza: {setting.default_deadline_hours}h
                            </span>
                            {setting.auto_assign && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                                Auto-assegna
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteProjectSetting(setting.project_name)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ============================================ */}
        {/* SMTP Configuration Tab */}
        {/* ============================================ */}
        <TabsContent value="smtp" className="mt-6">
          <div className="space-y-6">
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)]">
                    Configurazione Email SMTP
                  </h3>
                  <p className="text-sm text-[rgb(161,161,170)] font-mono mt-1">
                    Configura il server SMTP per l&apos;invio dei PIN di accesso ai clienti
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono text-[rgb(161,161,170)]">
                    {smtpSettings.enabled ? 'Attivo' : 'Disattivo'}
                  </span>
                  <Switch
                    checked={smtpSettings.enabled}
                    onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, enabled: checked})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Server SMTP *</label>
                  <Input
                    value={smtpSettings.host}
                    onChange={(e) => setSmtpSettings({...smtpSettings, host: e.target.value})}
                    placeholder="smtp.gmail.com"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Porta *</label>
                  <Input
                    type="number"
                    value={smtpSettings.port}
                    onChange={(e) => setSmtpSettings({...smtpSettings, port: parseInt(e.target.value) || 587})}
                    placeholder="587"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Username</label>
                  <Input
                    value={smtpSettings.username}
                    onChange={(e) => setSmtpSettings({...smtpSettings, username: e.target.value})}
                    placeholder="user@example.com"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Password</label>
                  <Input
                    type="password"
                    value={smtpSettings.password}
                    onChange={(e) => setSmtpSettings({...smtpSettings, password: e.target.value})}
                    placeholder="••••••••"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Email Mittente *</label>
                  <Input
                    type="email"
                    value={smtpSettings.from_email}
                    onChange={(e) => setSmtpSettings({...smtpSettings, from_email: e.target.value})}
                    placeholder="noreply@example.com"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Nome Mittente</label>
                  <Input
                    value={smtpSettings.from_name}
                    onChange={(e) => setSmtpSettings({...smtpSettings, from_name: e.target.value})}
                    placeholder="MantisBT Portal"
                    className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={smtpSettings.use_plain || false}
                    onCheckedChange={(checked) => setSmtpSettings({
                      ...smtpSettings, 
                      use_plain: checked,
                      use_tls: checked ? false : smtpSettings.use_tls,
                      use_ssl: checked ? false : smtpSettings.use_ssl
                    })}
                  />
                  <span className="text-sm font-mono text-[rgb(161,161,170)]">
                    Senza crittografia (porta 25)
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={smtpSettings.use_tls && !smtpSettings.use_plain}
                    onCheckedChange={(checked) => setSmtpSettings({
                      ...smtpSettings, 
                      use_tls: checked,
                      use_plain: checked ? false : smtpSettings.use_plain,
                      use_ssl: checked ? false : smtpSettings.use_ssl
                    })}
                  />
                  <span className="text-sm font-mono text-[rgb(161,161,170)]">
                    TLS/STARTTLS (porta 587)
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={smtpSettings.use_ssl || false}
                    onCheckedChange={(checked) => setSmtpSettings({
                      ...smtpSettings, 
                      use_ssl: checked,
                      use_tls: checked ? false : smtpSettings.use_tls,
                      use_plain: checked ? false : smtpSettings.use_plain
                    })}
                  />
                  <span className="text-sm font-mono text-[rgb(161,161,170)]">
                    SSL (porta 465)
                  </span>
                </div>
              </div>

              {/* Test result */}
              {smtpTestResult && (
                <div className={`mt-6 p-4 rounded-lg border ${smtpTestResult.success ? 'bg-green-500/10 border-green-500' : 'bg-red-500/10 border-red-500'}`}>
                  <div className="flex items-center gap-2">
                    {smtpTestResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`font-mono text-sm ${smtpTestResult.success ? 'text-green-500' : 'text-red-500'}`}>
                      {smtpTestResult.message}
                    </span>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={handleTestSmtp}
                  disabled={loading || smtpTesting || !smtpSettings.host || !smtpSettings.from_email}
                  className="font-mono border-[rgb(63,63,70)] text-[rgb(250,250,250)]"
                >
                  {smtpTesting ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Test Connessione
                </Button>
                <Button
                  onClick={handleSaveSmtpSettings}
                  disabled={loading}
                  className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Salva Configurazione
                </Button>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-400 mt-0.5" />
                <div>
                  <h4 className="font-mono font-bold text-blue-400">Informazioni SMTP</h4>
                  <ul className="text-sm text-[rgb(161,161,170)] font-mono mt-2 space-y-1">
                    <li>• <strong>Gmail</strong>: smtp.gmail.com, porta 587, TLS attivo. Usa una password per app.</li>
                    <li>• <strong>Office 365</strong>: smtp.office365.com, porta 587, TLS attivo.</li>
                    <li>• <strong>Custom</strong>: Verifica le impostazioni con il tuo provider.</li>
                    <li>• Se SMTP è disabilitato, i PIN verranno solo loggati (per sviluppo).</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ============================================ */}
        {/* Google Calendar Tab */}
        {/* ============================================ */}
        <TabsContent value="google" className="mt-6">
          <div className="space-y-6">
            <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)]">
                    Integrazione Google Calendar
                  </h3>
                  <p className="text-sm text-[rgb(161,161,170)] font-mono mt-1">
                    Collega il tuo account Google per aggiungere le issue come eventi nel calendario
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {googleStatus.connected ? (
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-mono">
                      Connesso
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-mono">
                      Non connesso
                    </span>
                  )}
                </div>
              </div>

              {googleStatus.connected ? (
                <div className="space-y-4">
                  {/* Connected account info */}
                  <div className="bg-[rgb(39,39,42)]/50 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[rgb(59,130,246)]/20 rounded-full flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-[rgb(59,130,246)]" />
                      </div>
                      <div>
                        <p className="text-[rgb(250,250,250)] font-mono font-semibold">
                          {googleStatus.google_email || 'Account Google'}
                        </p>
                        <p className="text-sm text-[rgb(161,161,170)] font-mono">
                          Calendario connesso
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Info about usage */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="font-mono font-bold text-blue-400">Come usare</h4>
                        <p className="text-sm text-[rgb(161,161,170)] font-mono mt-1">
                          Ora puoi aggiungere le issue al tuo Google Calendar! Cerca l&apos;icona 
                          <Calendar className="h-4 w-4 inline mx-1" /> 
                          nella lista delle issue o nella pagina di dettaglio.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Disconnect button */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={handleDisconnectGoogle}
                      disabled={googleLoading}
                      className="font-mono border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      {googleLoading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <X className="h-4 w-4 mr-2" />
                      )}
                      Disconnetti
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Connect prompt */}
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[rgb(39,39,42)] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="h-8 w-8 text-[rgb(161,161,170)]" />
                    </div>
                    <h4 className="text-lg font-mono font-bold text-[rgb(250,250,250)] mb-2">
                      Connetti Google Calendar
                    </h4>
                    <p className="text-sm text-[rgb(161,161,170)] font-mono mb-6 max-w-md mx-auto">
                      Collega il tuo account Google per pianificare le attività relative alle issue 
                      direttamente nel tuo calendario.
                    </p>
                    <Button
                      onClick={handleConnectGoogle}
                      disabled={googleLoading}
                      className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
                    >
                      {googleLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Connessione...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4 mr-2" />
                          Connetti con Google
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Info box */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="font-mono font-bold text-yellow-400">Permessi richiesti</h4>
                        <ul className="text-sm text-[rgb(161,161,170)] font-mono mt-2 space-y-1">
                          <li>• Visualizzare e modificare gli eventi del calendario</li>
                          <li>• Leggere l&apos;indirizzo email del tuo account Google</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Settings;
